import Collapse from '@material-ui/core/Collapse'
import Grid from '@material-ui/core/Grid'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import RrButton from '../RrButton'
import { nextStep, prevStep, MATCHING_COLUMNS } from './helpers'

const UPLOAD_TYPE_LABELS = {
  new: 'Add new people, and update anyone we already have',
  update: 'Just updating existing people',
}

function getAvailableMatchFields(csvHeaders) {
  const normalised = csvHeaders.map((h) => h.trim().toLowerCase())
  return MATCHING_COLUMNS
    .filter(({ label }) => normalised.includes(label.toLowerCase()))
    .map(({ field }) => field)
}

// Returns all orderings of subsets, longest first. Max 2 fields supported for now.
function allOrderings(fields) {
  if (fields.length === 2) {
    const [a, b] = fields
    return [[a, b], [b, a], [a], [b]]
  }
  return fields.map((f) => [f])
}

function buildMatchOptions(availableFields) {
  const nonIdFields = availableFields.filter((f) => f !== 'external_id')
  const orderings = allOrderings(nonIdFields)
  if (availableFields.includes('external_id')) {
    return [...orderings.map((order) => ['external_id', ...order]), ['external_id']]
  }
  return orderings
}

function matchFieldsToLabel(fields) {
  return fields.map((f) => MATCHING_COLUMNS.find((c) => c.field === f)?.label).join(', then ')
}

const MatchStrategy = ({
  show,
  step,
  steps,
  csv,
  csvHeaders,
  matchFields,
  setMatchFields,
  uploadType,
  onCancel,
  onComplete,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)

  const availableMatchFields = getAvailableMatchFields(csvHeaders)
  const matchOptions = buildMatchOptions(availableMatchFields)
  const hasExternalId = availableMatchFields.includes('external_id')
  const showChangeDropdown = matchOptions.length > 1
  const activeMatchFields = matchFields.length > 0 ? matchFields : availableMatchFields

  const nonIdFields = availableMatchFields.filter((f) => f !== 'external_id')
  const nonIdMatchLabel = nonIdFields.length === 2 ? 'emails and/or phone numbers' : nonIdFields[0] === 'email' ? 'emails' : 'phone numbers'
  const nonIdHelpText = hasExternalId
    ? `Choose whether to also check for records with matching ${nonIdMatchLabel}.`
    : `Choose whether to match by ${nonIdMatchLabel}.`

  const explanatoryText = hasExternalId
    ? "We picked this based on the columns in your file. Change it if your team usually does it differently."
    : `Your file has no Membership Number column, so we'll match using ${availableMatchFields.map((f) => MATCHING_COLUMNS.find((c) => c.field === f)?.label).join(' then ')}.`

  const filename = csv?.ref?.name || 'CSV file'

  return (
    <Collapse in={show} className="cc-content-container">
      <Grid item container direction="column" spacing={2}>
        <Grid item>
          <div className="cc-match-box info margin-top margin-bottom">
            <div className="cc-match-summary-row">
              <span className="cc-match-summary-label caption dark-grey bold">FILE</span>
              <span>{filename}</span>
            </div>
            <div className="cc-match-summary-row">
              <span className="cc-match-summary-label caption dark-grey bold">MODE</span>
              <span>{UPLOAD_TYPE_LABELS[uploadType] || ''}</span>
            </div>
          </div>
        </Grid>

        <Grid item>
          <h3 className="no-margin-top margin-bottom">How we'll match to existing people</h3>
          <p className="caption dark-grey margin-bottom">
            {showChangeDropdown && hasExternalId && 'Records will first be matched by Membership Number. '}
            {showChangeDropdown && nonIdFields.length > 0 && `${nonIdHelpText} `}
            If a match is found, the matching record will be updated with the data in this upload. If a match is not found a new record is created.
          </p>
          <div className="cc-match-box">
            <SvgIconsLinkChain className="dark-grey" />
            <p className="grow no-margin-top no-margin-bottom">
              People uploaded will be matched to existing supporters using{' '}
              <strong>{matchFieldsToLabel(activeMatchFields)}</strong>.
            </p>
            {showChangeDropdown && (
              <button
                className="cc-match-change"
                onClick={(e) => setMenuAnchorEl(e.currentTarget)}
              >
                Change <span className="dropdown-caret">▾</span>
              </button>
            )}
          </div>
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={() => setMenuAnchorEl(null)}
          >
            {matchOptions.map((option) => (
              <MenuItem
                key={option.join(',')}
                selected={option.join(',') === activeMatchFields.join(',')}
                onClick={() => {
                  setMatchFields(option)
                  setMenuAnchorEl(null)
                }}
              >
                {matchFieldsToLabel(option)}
              </MenuItem>
            ))}
          </Menu>
          <p className="caption dark-grey margin-top half">{explanatoryText}</p>
        </Grid>

        <Grid
          item
          style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', paddingTop: 25 }}
        >
          <RrButton
            title={prevStep(step, steps)}
            onClick={onCancel}
            type="secondary"
            icon="back"
          />
          <RrButton
            title={nextStep(step, steps)}
            onClick={onComplete}
            type="primary"
            disabled={activeMatchFields.length === 0}
          />
        </Grid>
      </Grid>
    </Collapse>
  )
}

export default MatchStrategy
